/**
 * Minimal cent-exact VAT split for Progear Finance 2.0.
 *
 * SCOPE (replit.md L9/L11 + Glenn-approved, 2026-06-24): module 1.1 (the
 * cash-flow planner) needs to turn a single entered amount into the canonical
 * NET / VAT / GROSS `Money` so a manual entry can be stored. This file is ONLY
 * that primitive: a net↔gross split at a given whole-number rate, computed in
 * integer cents (BigInt internally, never a JS float for money). The FULL VAT
 * engine — domestic/foreign/reduced-rate policy, the `/1.24` extraction rules,
 * and the golden suite that proves them — is module 1.2 and is built on top of
 * this, not duplicated. Do not grow rate-policy logic here; add it in 1.2.
 *
 * Rounding is half-away-from-zero so positive and negative amounts (e.g. credit
 * notes) are symmetric, and the GROSS = NET + VAT identity always holds exactly
 * because the residual side is taken as the difference, never rounded twice.
 */

import type { Money, VatMode } from './money';
import { isWholeCents } from './money';

/** Which part of the amount the caller entered. */
export type VatBasis = 'net' | 'gross';

export interface VatSplitInput {
  /** The entered amount in integer cents (net or gross per `basis`). */
  amountCents: number;
  /** Whether `amountCents` is the net or the gross amount. */
  basis: VatBasis;
  /** VAT rate as a whole-number percentage (e.g. 24, 13, 9, 0). */
  vatRate: number;
  /** How the rate is applied (default `domestic`); carried onto the Money. */
  vatMode?: VatMode;
}

/**
 * Integer division rounded half-away-from-zero. `denom` is always positive here.
 * The result is always between 0 and |numer| (a fraction of a validated whole-cent
 * amount), so it never leaves the safe-integer range.
 */
function roundedDiv(numer: bigint, denom: bigint): number {
  const q = numer / denom;
  const rem = numer % denom;
  const absRemTwice = (rem < 0n ? -rem : rem) * 2n;
  const rounded = absRemTwice >= denom ? q + (numer < 0n ? -1n : 1n) : q;
  return Number(rounded);
}

/**
 * Split an entered amount into a `Money` (net/vat/gross in integer cents) at a
 * whole-number VAT rate. `net` basis adds VAT on top; `gross` basis extracts the
 * VAT already inside the amount. Throws on non-integer cents or an invalid rate
 * so bad data surfaces instead of silently producing a wrong amount.
 */
export function splitVat(input: VatSplitInput): Money {
  const { amountCents, basis, vatRate, vatMode = 'domestic' } = input;
  if (!isWholeCents(amountCents)) {
    throw new RangeError(`splitVat: expected whole cents, received ${amountCents}`);
  }
  if (!Number.isInteger(vatRate) || vatRate < 0) {
    throw new RangeError(`splitVat: vatRate must be a whole-number percentage >= 0, received ${vatRate}`);
  }

  const amount = BigInt(amountCents);
  const rate = BigInt(vatRate);

  if (basis === 'net') {
    const net = amountCents;
    const vat = roundedDiv(amount * rate, 100n);
    return { net, vat, gross: net + vat, vatRate, vatMode };
  }

  // gross basis: net is the gross with the VAT share removed; vat is the residual
  // so gross = net + vat holds exactly without a second rounding.
  const gross = amountCents;
  const net = roundedDiv(amount * 100n, 100n + rate);
  return { net, vat: gross - net, gross, vatRate, vatMode };
}

/** An externally-supplied NET + GROSS pair (integer cents) plus its VAT metadata. */
export interface NetGrossInput {
  /** Net amount in integer cents (e.g. an Erply sales document's netTotal). */
  netCents: number;
  /** Gross amount in integer cents (e.g. an Erply sales document's total). */
  grossCents: number;
  /** VAT rate as a whole-number percentage (e.g. 24, 13, 9, 0). */
  vatRate: number;
  /** How the rate is applied (default `domestic`); carried onto the Money. */
  vatMode?: VatMode;
}

/**
 * Assemble a `Money` from an externally-supplied NET and GROSS pair, in integer
 * cents. VAT is taken as the residual `gross − net` — never a second rounding — so
 * the gross = net + vat identity always holds EXACTLY even when the source system
 * rounded its own VAT line differently (an at-least-once mirror must not drift a
 * cent). Sign-safe: a credit note whose net/gross are both negative keeps its sign
 * and a negative residual VAT. Throws on non-integer cents or an invalid rate so
 * bad external data surfaces instead of silently producing a wrong amount. Used by
 * sync connectors so money assembly stays in finance-core (replit.md L9), never in
 * a route or a component.
 */
export function moneyFromNetGross(input: NetGrossInput): Money {
  const { netCents, grossCents, vatRate, vatMode = 'domestic' } = input;
  if (!isWholeCents(netCents)) {
    throw new RangeError(`moneyFromNetGross: expected whole net cents, received ${netCents}`);
  }
  if (!isWholeCents(grossCents)) {
    throw new RangeError(`moneyFromNetGross: expected whole gross cents, received ${grossCents}`);
  }
  if (!Number.isInteger(vatRate) || vatRate < 0) {
    throw new RangeError(`moneyFromNetGross: vatRate must be a whole-number percentage >= 0, received ${vatRate}`);
  }
  return { net: netCents, vat: grossCents - netCents, gross: grossCents, vatRate, vatMode };
}

/** A `Money` cut into the part that moves and the part left behind. */
export interface MoneySplit {
  /** The installment that moves to the new day. */
  moved: Money;
  /** What stays behind at the origin (the "jääk" ghost). */
  remainder: Money;
}

/**
 * Split a `Money` into a `moved` installment of `movedGrossCents` and the
 * `remainder` left at the origin, cent-exact. The moved part re-derives its VAT
 * from the same rate on a GROSS basis (`splitVat`); the remainder is taken as the
 * original MINUS the moved part component-by-component, so `moved + remainder`
 * reconstitutes the original EXACTLY and each part keeps the gross = net + vat
 * identity (the residual is never rounded twice). `movedGrossCents` is clamped to
 * the [0, gross] interval (orientation-safe, so credit notes don't invert).
 * Throws on non-integer cents so bad data surfaces instead of silently drifting.
 */
export function splitMoney(money: Money, movedGrossCents: number): MoneySplit {
  if (!isWholeCents(movedGrossCents)) {
    throw new RangeError(`splitMoney: expected whole cents, received ${movedGrossCents}`);
  }
  const lo = Math.min(0, money.gross);
  const hi = Math.max(0, money.gross);
  const movedGross = Math.max(lo, Math.min(movedGrossCents, hi));
  const moved = splitVat({ amountCents: movedGross, basis: 'gross', vatRate: money.vatRate, vatMode: money.vatMode });
  const remainder: Money = {
    net: money.net - moved.net,
    vat: money.vat - moved.vat,
    gross: money.gross - moved.gross,
    vatRate: money.vatRate,
    vatMode: money.vatMode,
  };
  return { moved, remainder };
}

/**
 * Carve a `Money` into a PAYMENT SCHEDULE — N `Money` parts whose GROSS amounts are
 * exactly `partGrossCents` (a part per scheduled instalment), cent-exact. Each part
 * before the last re-derives its VAT from the same rate on a gross basis (via
 * {@link splitMoney}); the FINAL part is whatever is left, so every part keeps the
 * gross = net + vat identity and the whole schedule reconstitutes the original
 * EXACTLY (net, vat AND gross each sum back — no rounding drift, the residual is
 * never rounded twice). This is the creation-time analogue of a drag-split: the same
 * finance-core money maths, done N-ways at once (replit.md L9 — money maths live
 * here, never in a route or a component). Every `partGrossCents` value must be a
 * whole number of cents, lie on the same side of zero as the total (so a credit note
 * stays negative), and the parts must sum to `money.gross` — otherwise it throws, so
 * an unbalanced schedule surfaces instead of silently producing a wrong amount.
 */
export function splitMoneyIntoParts(money: Money, partGrossCents: readonly number[]): Money[] {
  if (partGrossCents.length === 0) {
    throw new RangeError('splitMoneyIntoParts: at least one part is required');
  }
  const lo = Math.min(0, money.gross);
  const hi = Math.max(0, money.gross);
  let sum = 0;
  for (const cents of partGrossCents) {
    if (!isWholeCents(cents)) {
      throw new RangeError(`splitMoneyIntoParts: expected whole cents, received ${cents}`);
    }
    if (cents < lo || cents > hi) {
      throw new RangeError(`splitMoneyIntoParts: part ${cents} is outside the [${lo}, ${hi}] range`);
    }
    sum += cents;
  }
  if (sum !== money.gross) {
    throw new RangeError(`splitMoneyIntoParts: parts sum to ${sum}, expected gross ${money.gross}`);
  }

  const parts: Money[] = [];
  let remaining = money;
  for (let i = 0; i < partGrossCents.length - 1; i += 1) {
    const { moved, remainder } = splitMoney(remaining, partGrossCents[i]);
    parts.push(moved);
    remaining = remainder;
  }
  // The last part absorbs the accumulated residual so net/vat/gross reconcile exactly.
  parts.push(remaining);
  return parts;
}

/**
 * Spread a GROSS amount evenly across `count` instalments, in integer cents — the
 * money maths behind the schedule dialog's "Jaga võrdselt" helper (replit.md L9).
 * The base cent-amount is floored and the leftover cents are handed one-by-one to
 * the leading parts, so every part differs by at most one cent and the parts sum to
 * `grossCents` EXACTLY. Sign-safe (a credit note splits into negative parts). Throws
 * on non-integer cents or a non-positive count so bad input surfaces.
 */
export function evenGrossParts(grossCents: number, count: number): number[] {
  if (!isWholeCents(grossCents)) {
    throw new RangeError(`evenGrossParts: expected whole cents, received ${grossCents}`);
  }
  if (!Number.isInteger(count) || count < 1) {
    throw new RangeError(`evenGrossParts: count must be a positive integer, received ${count}`);
  }
  const sign = grossCents < 0 ? -1 : 1;
  const abs = Math.abs(grossCents);
  const base = Math.floor(abs / count);
  const residual = abs - base * count;
  const parts: number[] = [];
  for (let i = 0; i < count; i += 1) {
    parts.push(sign * (base + (i < residual ? 1 : 0)));
  }
  return parts;
}

/**
 * Sum a list of integer-cent amounts, cent-exact — the aggregate behind the schedule
 * dialog's reconciliation bar, so a component never adds monetary amounts itself
 * (replit.md L9). Throws on any non-integer cent so bad input surfaces instead of
 * silently drifting.
 */
export function sumCents(parts: readonly number[]): number {
  let sum = 0;
  for (const cents of parts) {
    if (!isWholeCents(cents)) {
      throw new RangeError(`sumCents: expected whole cents, received ${cents}`);
    }
    sum += cents;
  }
  return sum;
}

/** The reconciliation state of a set of schedule parts against an entry's total gross. */
export interface PartsReconciliation {
  /** Sum of the part gross-cents entered so far. */
  sum: number;
  /** `total − sum`: cents still to allocate (negative = over-allocated). */
  remaining: number;
  /** True when the parts sum EXACTLY to the total gross — a schedule ready to submit. */
  balanced: boolean;
}

/**
 * Reconcile a set of schedule part gross-cents against the entry's total gross — the
 * money maths behind the create dialog's "Osadena" reconciliation bar (sum · remaining
 * · balanced), so the component renders the state without computing money itself
 * (replit.md L9). Throws (via {@link sumCents}) on a non-integer part.
 */
export function reconcileParts(totalGrossCents: number, parts: readonly number[]): PartsReconciliation {
  const sum = sumCents(parts);
  const remaining = totalGrossCents - sum;
  return { sum, remaining, balanced: remaining === 0 };
}
